import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import {
  BrushZoomConfig,
  DailyGreeks,
  TermStructure,
  TermStructureConfig,
} from 'types';
import { useTheme } from '@mui/material/styles';
import { DEFAULT_BRUSH_ZOOM_CONFIG } from 'config';
import { getQueryDate } from 'util/shared';
import { getNextAvailableColor } from 'util/iVol';

interface TermStructureContextValue {
  selectedSym: string;

  config: TermStructureConfig;
  updateConfig: (patch: Partial<TermStructureConfig>) => void;

  selectedDates: DailyGreeks[];
  setSelectedDates: Dispatch<SetStateAction<DailyGreeks[]>>;

  zoomConfig: BrushZoomConfig;
  setZoomConfig: Dispatch<SetStateAction<BrushZoomConfig>>;

  chartData: TermStructure[];
  setChartData: Dispatch<SetStateAction<TermStructure[]>>;

  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;

  chartRef: MutableRefObject<HTMLDivElement | null>;
}

const TermStructureContext = createContext<TermStructureContextValue | null>(
  null,
);

interface TermStructureProviderProps {
  selectedSym: string;
  config: TermStructureConfig;
  onConfigChange: (patch: Partial<TermStructureConfig>) => void;
  children: ReactNode;
  // Today's line is built here, not from config, so it needs its own way to seed as precomputed.
  precomputedTodayTermStructure?: TermStructure[];
  todayLegendLabel?: string;
}

export const TermStructureProvider = ({
  selectedSym,
  config,
  onConfigChange,
  children,
  precomputedTodayTermStructure,
  todayLegendLabel,
}: TermStructureProviderProps) => {
  const theme = useTheme();
  const availableColors = Object.values(theme.palette.iVol.termStructure);

  const [selectedDates, setSelectedDates] = useState<DailyGreeks[]>(() => {
    const todayKey = getQueryDate(true).format('YYYY-MM-DD');

    // Pre-claim every color already assigned to a saved entry so today's line
    // — and any newly-assigned entry — won't collide with them.
    const usedColors: string[] = config.additionalDates
      .map((e) => e.color)
      .filter((c): c is string => c != null && c !== '');

    // Today's line: pick the first palette color not already used by a saved
    // entry, falling back to the first slot if the palette is exhausted.
    const todayColor =
      getNextAvailableColor(usedColors, availableColors) ??
      availableColors[0] ??
      theme.palette.primary.main;
    usedColors.push(todayColor);

    // Additional dates: keep persisted color or assign next available
    const additionalDates = config.additionalDates.map((entry) => {
      const color =
        entry.color && entry.color !== ''
          ? entry.color
          : (getNextAvailableColor(usedColors, availableColors) ??
            theme.palette.primary.main);
      usedColors.push(color);
      return {
        ...entry,
        data: [],
        color,
      };
    });

    return [
      {
        key: todayKey,
        tradeDate: getQueryDate(true),
        data: [],
        color: todayColor,
        currentPrice: 0,
        precomputedTermStructure: precomputedTodayTermStructure,
        legendLabel: todayLegendLabel,
      },
      ...additionalDates,
    ];
  });

  const [zoomConfig, setZoomConfig] = useState<BrushZoomConfig>(
    DEFAULT_BRUSH_ZOOM_CONFIG,
  );

  const [chartData, setChartData] = useState<TermStructure[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chartRef = useRef<HTMLDivElement | null>(null);

  return (
    <TermStructureContext.Provider
      value={{
        selectedSym,
        config,
        updateConfig: onConfigChange,
        selectedDates,
        setSelectedDates,
        zoomConfig,
        setZoomConfig,
        chartData,
        setChartData,
        loading,
        setLoading,
        error,
        setError,
        chartRef,
      }}
    >
      {children}
    </TermStructureContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTermStructureCtx = (): TermStructureContextValue => {
  const ctx = useContext(TermStructureContext);
  if (ctx == null) {
    throw new Error(
      'useTermStructureCtx must be used within a TermStructureProvider',
    );
  }
  return ctx;
};
